<template>
  <dashboard-layout title="Overview" kicker="Groomer dashboard" :items="navItems">
    <div class="row g-3">
      <div class="col-xl-3 col-lg-6 col-12" v-for="c in statCards" :key="c.id">
        <div class="dash-stat" :class="c.variant">
          <div class="dash-stat-top">
            <div class="dash-stat-label inter-regular-family">{{ c.label }}</div>
            <div class="dash-stat-ic" aria-hidden="true"><i :class="c.icon"></i></div>
          </div>
          <div class="dash-stat-value filson-black-family">{{ c.value }}</div>
          <div class="dash-stat-sub inter-regular-family">{{ c.sub }}</div>
        </div>
      </div>
      <div v-if="statsError" class="col-12">
        <div class="small text-muted">{{ statsError }}</div>
      </div>
    </div>

    <div class="row g-3 mt-1">
      <div class="col-xl-4 col-lg-6 col-12" v-for="card in mainCards" :key="card.id">
        <div class="dash-card bg-white">
          <div class="dash-card-head">
            <div class="dash-card-title filson-black-family">{{ card.title }}</div>
          </div>
          <div class="dash-card-body inter-regular-family text-muted">
            {{ card.body }}
          </div>
        </div>
      </div>
    </div>
  </dashboard-layout>
</template>

<script>
import DashboardLayout from './DashboardLayout.vue';
import { GROOMER_DASH_NAV } from '@/utils/dashboardNav';

export default {
  name: 'GroomerDashboard',
  components: { DashboardLayout },
  data() {
    return {
      navItems: GROOMER_DASH_NAV,
      statsLoading: true,
      statsError: '',
      earningsTotal: 0,
      completedJobCount: 0,
      pendingAssignmentCount: 0,
      inProgressCount: 0,
      mainCards: [
        { id: 'c1', title: 'Today’s Schedule', body: 'Quick view of your upcoming appointments.' },
        { id: 'c2', title: 'Client Messages', body: 'See recent messages and requests.' },
        { id: 'c3', title: 'Payouts', body: 'Track your payout status and history.' },
        { id: 'c4', title: 'Availability', body: 'Update your working hours and service areas.' },
        { id: 'c5', title: 'Top Services', body: 'Your most booked services in the last 7 days.' },
        { id: 'c6', title: 'Profile', body: 'Keep your profile updated for better conversions.' },
      ],
    };
  },
  computed: {
    statCards() {
      const loading = this.statsLoading;
      const ph = loading ? '…' : null;
      return [
        {
          id: 'earn',
          label: 'Earnings',
          value: ph ?? this.fmtMoney(this.earningsTotal),
          sub: 'Total from completed jobs',
          icon: 'fa fa-line-chart',
          variant: 'dash-stat--yellow',
        },
        {
          id: 'jobs',
          label: 'Completed jobs',
          value: ph ?? String(this.completedJobCount),
          sub: 'Marked complete (same as Earnings page)',
          icon: 'fa fa-check',
          variant: 'dash-stat--blue',
        },
        {
          id: 'pending',
          label: 'Awaiting response',
          value: ph ?? String(this.pendingAssignmentCount),
          sub: 'Assignments to accept or decline',
          icon: 'fa fa-bell',
          variant: 'dash-stat--teal',
        },
        {
          id: 'active',
          label: 'In progress',
          value: ph ?? String(this.inProgressCount),
          sub: 'Accepted, not completed yet',
          icon: 'fa fa-clock-o',
          variant: 'dash-stat--orange',
        },
      ];
    },
  },
  mounted() {
    this.loadOverviewStats();
  },
  methods: {
    fmtMoney(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'AED' }).format(n);
      } catch (_) {
        return `${n.toFixed(2)} AED`;
      }
    },
    normalizeAssignmentStatus(b) {
      const s = String(b?.partner_assignment_status || 'pending').toLowerCase().trim();
      if (s === 'accepted' || s === 'declined') return s;
      return 'pending';
    },
    isJobCompleted(b) {
      return String(b?.booking_status || '').toLowerCase().trim() === 'completed';
    },
    async loadOverviewStats() {
      this.statsLoading = true;
      this.statsError = '';
      try {
        const [earRes, listRes] = await Promise.all([
          this.$axios.get('/partner/earnings'),
          this.$axios.get('/partner/assigned-bookings'),
        ]);
        this.earningsTotal = Number(earRes?.data?.total_earnings) || 0;
        this.completedJobCount = Number(earRes?.data?.count) || 0;
        const bookings = Array.isArray(listRes?.data?.bookings) ? listRes.data.bookings : [];
        let pending = 0;
        let inProgress = 0;
        bookings.forEach((b) => {
          const pas = this.normalizeAssignmentStatus(b);
          if (pas === 'pending') pending += 1;
          if (pas === 'accepted' && !this.isJobCompleted(b)) inProgress += 1;
        });
        this.pendingAssignmentCount = pending;
        this.inProgressCount = inProgress;
      } catch (_) {
        this.statsError = 'Could not load live stats. Open Appointments / Earnings after signing in.';
        this.earningsTotal = 0;
        this.completedJobCount = 0;
        this.pendingAssignmentCount = 0;
        this.inProgressCount = 0;
      } finally {
        this.statsLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Reuse the same card styles as salon dashboard for consistency */
.dash-stat {
  border-radius: 14px;
  padding: 14px 14px 12px;
  color: #111;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
  min-height: 112px;
}
.dash-stat-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
.dash-stat-label {
  font-size: 12px;
  opacity: 0.9;
}
.dash-stat-ic {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.dash-stat-value {
  font-size: 26px;
  line-height: 1.1;
  margin-top: 6px;
}
.dash-stat-sub {
  font-size: 11px;
  opacity: 0.85;
  margin-top: 2px;
}
.dash-stat--orange { background: #ff7a2b; color: #fff; }
.dash-stat--orange .dash-stat-ic { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.18); }
.dash-stat--yellow { background: #ffcc00; }
.dash-stat--teal { background: #02b5ad; color: #fff; }
.dash-stat--teal .dash-stat-ic { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.18); }
.dash-stat--blue { background: #2f6cff; color: #fff; }
.dash-stat--blue .dash-stat-ic { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.18); }

.dash-card {
  border-radius: 14px;
  padding: 16px 16px 14px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  min-height: 106px;
}
.dash-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}
.dash-card-title {
  font-size: 14px;
  color: #111;
}
.dash-card-body {
  font-size: 12px;
  line-height: 1.5;
}
</style>

